var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { classNames, useEffect, useMemo, Fragment, useRef } from 'libs/react';
import { useState } from 'libs/react';
import { Image } from 'components/Image';
import { TabPanel } from 'components/TabPanel';
import { TabPanelGroup } from 'components/TabPanelGroup';
import { Text } from 'components/Text';
import { React } from 'libs/react';
import { Table } from 'components/table/Table';
import { Button } from 'components/Button';
import { TableValue } from 'components/table/TableValue';
import { Spinner } from 'components/Spinner';
import { useUser } from 'components/user/UserProvider';
import i18n from 'components/translation/i18n';
import { asset } from 'libs/asset';
import { formatFieldValue, hasDisplayableCustomFieldValue } from 'helpers/Field';
import { useModal } from 'components/modal/ModalStack';
import { useDomain } from 'components/domain/DomainProvider';
import { imageSources, thumbnailSource, VIEWER_SIZES } from 'helpers/mediaSources';
import { QuoteLink } from 'modules/public/quote/components/QuoteLink';
import './SaleItemViewer.scss';
/**
 * @component SaleItemViewer
 * @since 1.0.0
 */
export function SaleItemViewer(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const { style, has3DViewer, item, isQuote, isStore, lang, variant, proposed, alternativeTo } = Object.assign({}, props);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [loadingRow, setLoadingRow] = useState('');
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const user = useUser();
    const modal = useModal();
    const ref = useRef(null);
    const itemFieldValue = item.fieldValues
        ? item.fieldValues.filter((value) => {
            return (value.field.group.category == 'PRODUCT' &&
                value.field.visibility == 'PUBLIC');
        })
        : [];
    const langCode = lang || i18n.language;
    const detailFields = itemFieldValue.filter((value) => hasDisplayableCustomFieldValue(value.value, value.field, langCode));
    const variantDetailFields = (_b = (_a = variant === null || variant === void 0 ? void 0 : variant.fieldValues) === null || _a === void 0 ? void 0 : _a.filter((value) => {
        var _a;
        return ((_a = value.field.group) === null || _a === void 0 ? void 0 : _a.category) !== 'PRODUCT_CONFIGURATION' &&
            hasDisplayableCustomFieldValue(value.value, value.field, langCode);
    })) !== null && _b !== void 0 ? _b : [];
    const translation = (_c = item.translations) === null || _c === void 0 ? void 0 : _c.find((t) => t.language.code === langCode);
    const includedOptions = (_e = (_d = item.options) === null || _d === void 0 ? void 0 : _d.filter((option) => { var _a; return option.included && ((_a = option.item) === null || _a === void 0 ? void 0 : _a.id); })) !== null && _e !== void 0 ? _e : [];
    const showIncludedOptionsTab = isStore && includedOptions.length > 0;
    /**
     * @function onViewFile
     * @since 1.0.o
     * @hidden
     */
    const onViewFile = (value) => __awaiter(this, void 0, void 0, function* () {
        modal.present({
            title: value.file.filename,
            picto: '',
            className: 'deal-sale-item-selector-item-modal',
            modal: (React.createElement("div", { className: "deal-sale-item-selector-item-modal-file" },
                React.createElement("embed", { src: value.file.url, width: "100%", height: "100%" })))
        });
    });
    const isContactUser = user.isContactUser();
    /**
     * Files are always filtered by language (a file without a language is
     * considered language-agnostic and always shown). On top of that,
     * visibility is restricted based on who is viewing the item:
     * - Contact (client portal) users only see PUBLIC/PARTNER files.
     * - Public-facing contexts (quote, store) only see PUBLIC files.
     * - Everyone else (internal staff, catalog, deal pickers, etc.) sees all files.
     */
    const filesFiltered = useMemo(() => {
        if (!item.files) {
            return [];
        }
        const matchesLanguage = (file) => !file.language || file.language.code === langCode;
        if (isContactUser) {
            return item.files.filter((file) => (file.visibility === 'PUBLIC' || file.visibility === 'PARTNER') &&
                matchesLanguage(file));
        }
        if (isQuote || isStore) {
            return item.files.filter((file) => file.visibility === 'PUBLIC' && matchesLanguage(file));
        }
        return item.files.filter(matchesLanguage);
    }, [item.files, isContactUser, isQuote, isStore, langCode]);
    const summary = (_f = translation === null || translation === void 0 ? void 0 : translation.summary) !== null && _f !== void 0 ? _f : item.summary;
    const infos = (_h = (_g = translation === null || translation === void 0 ? void 0 : translation.infos) !== null && _g !== void 0 ? _g : item.infos) !== null && _h !== void 0 ? _h : '';
    /**
     * The description is clamped by CSS while collapsed; its content (HTML,
     * images) can grow after mount, so overflow is re-measured on resize.
     */
    useEffect(() => {
        const node = ref.current;
        if (!node || isExpanded) {
            return;
        }
        const measure = () => {
            setIsOverflowing(node.scrollHeight > node.clientHeight + 1);
        };
        measure();
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(measure);
        Array.from(node.children).forEach((child) => observer.observe(child));
        return () => observer.disconnect();
    }, [selectedTabIndex, isExpanded, summary, infos]);
    const schema = {
        c1: {
            column: 'fileName',
            header: 'FileName',
            footer: 'FileName',
            render: (value) => {
                return (React.createElement(Fragment, null,
                    React.createElement("div", { className: "sale-item-viewer-file-name", onClick: () => onViewFile(value) },
                        React.createElement(TableValue, { value: value.file.filename }))));
            }
        },
        c2: {
            column: 'actions',
            type: 'actions',
            header: '',
            footer: '',
            render: (value) => {
                return loadingRow !== value.id ? (React.createElement(Button, { size: "picto", picto: "eye", onPress: () => onViewFile(value) })) : (React.createElement(Spinner, null));
            }
        }
    };
    const className = classNames('sale-item-viewer', style && 'sale-item-viewer--' + style, { 'sale-item-viewer--no-media': item.image == null }, props.className);
    return (React.createElement("div", { className: className },
        React.createElement("div", { className: "sale-item-viewer-main" },
            React.createElement("div", { className: "sale-item-viewer-head" },
                React.createElement(SaleItemViewerGallery, { item: item, has3DViewer: has3DViewer })),
            React.createElement("div", { className: "sale-item-viewer-body" },
                (proposed || alternativeTo) && (React.createElement("div", { className: "sale-item-viewer-badges" },
                    proposed && (React.createElement(Text, { className: "sale-item-viewer-badge", value: t('Suggestion Only') })),
                    alternativeTo && (React.createElement(Text, { className: "sale-item-viewer-badge sale-item-viewer-badge--alternative", value: t('Alternative to') + ' ' + alternativeTo })))),
                React.createElement(Text, { className: "sale-item-viewer-title", value: (_j = translation === null || translation === void 0 ? void 0 : translation.title) !== null && _j !== void 0 ? _j : item.title }),
                React.createElement(TabPanelGroup, { selectedTabIndex: selectedTabIndex, setSelectedTabIndex: setSelectedTabIndex },
                    React.createElement(TabPanel, { className: "sale-item-viewer-panel", label: t('Description') },
                        React.createElement("div", { className: classNames('sale-item-viewer-panel-content', {
                                'sale-item-viewer-description--clamped': !isExpanded,
                                'sale-item-viewer-description--faded': !isExpanded && isOverflowing
                            }), ref: ref },
                            React.createElement(Text, { className: "sale-item-viewer-summary", html: true, value: summary }),
                            React.createElement("div", { className: "sale-item-viewer-infos", dangerouslySetInnerHTML: { __html: infos } })),
                        (isOverflowing || isExpanded) && (React.createElement(Button, { className: "sale-item-viewer-show-more", size: "small", label: isExpanded ? t('Less') : t('More'), picto: isExpanded ? 'chevron-up' : 'chevron-down', onPress: () => setIsExpanded(!isExpanded) })),
                        React.createElement("div", { className: "sale-item-viewer-panel-content-promoted-fields" },
                            item.fieldValues &&
                                item.fieldValues
                                    .filter((fv) => fv.field.promote &&
                                    hasDisplayableCustomFieldValue(fv.value, fv.field, langCode))
                                    .map((field, index) => (React.createElement("div", { key: index },
                                    React.createElement(Text, { className: "sale-item-viewer-panel-content-promoted-fields-title", value: field.field.title }),
                                    field.field.type === 'URL' ? (React.createElement(QuoteLink, { value: formatFieldValue(field.value, field.field.type, field.field) })) : (React.createElement(Text, { className: "sale-item-viewer-panel-content-promoted-fields-value", value: formatFieldValue(field.value, field.field.type, field.field, langCode) }))))),
                            (variant === null || variant === void 0 ? void 0 : variant.fieldValues) &&
                                variant.fieldValues
                                    .filter((fv) => fv.field.promote &&
                                    hasDisplayableCustomFieldValue(fv.value, fv.field, langCode))
                                    .map((field, index) => (React.createElement("div", { key: index },
                                    React.createElement(Text, { className: "sale-item-viewer-panel-content-promoted-fields-title", value: field.field.title }),
                                    React.createElement(Text, { className: "sale-item-viewer-panel-content-promoted-fields-value", value: formatFieldValue(field.value, field.field.type, field.field, langCode) })))))),
                    showIncludedOptionsTab && (React.createElement(TabPanel, { className: "sale-item-viewer-panel", label: t('Inclusions') },
                        React.createElement("div", { className: "sale-item-viewer-panel-content sale-item-viewer-panel-content--scrollable" },
                            React.createElement("ul", { className: "sale-item-viewer-included-options" }, includedOptions.map((option) => {
                                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
                                const optionTranslation = (_b = (_a = option.item) === null || _a === void 0 ? void 0 : _a.translations) === null || _b === void 0 ? void 0 : _b.find((entry) => entry.language.code === langCode);
                                const optionTitle = (_e = (_c = optionTranslation === null || optionTranslation === void 0 ? void 0 : optionTranslation.title) !== null && _c !== void 0 ? _c : (_d = option.item) === null || _d === void 0 ? void 0 : _d.title) !== null && _e !== void 0 ? _e : '';
                                const optionSummary = (_f = optionTranslation === null || optionTranslation === void 0 ? void 0 : optionTranslation.summary) !== null && _f !== void 0 ? _f : (_g = option.item) === null || _g === void 0 ? void 0 : _g.summary;
                                return (React.createElement("li", { key: option.id, className: "sale-item-viewer-included-option" },
                                    React.createElement("div", { className: "sale-item-viewer-included-option__image" }, ((_j = (_h = option.item) === null || _h === void 0 ? void 0 : _h.image) === null || _j === void 0 ? void 0 : _j.image.url) ? (React.createElement(Image, { lazy: true, source: thumbnailSource(option.item.image.image) })) : (React.createElement("span", { className: "sale-item-viewer-included-option__initial" }, (_l = (_k = optionTitle[0]) === null || _k === void 0 ? void 0 : _k.toUpperCase()) !== null && _l !== void 0 ? _l : '?'))),
                                    React.createElement("div", { className: "sale-item-viewer-included-option__content" },
                                        React.createElement("div", { className: "sale-item-viewer-included-option__title-row" },
                                            React.createElement(Text, { className: "sale-item-viewer-included-option__title", value: optionTitle }),
                                            option.quantity > 1 && (React.createElement(Text, { className: "sale-item-viewer-included-option__quantity", value: `× ${option.quantity}` }))),
                                        optionSummary && (React.createElement(Text, { className: "sale-item-viewer-included-option__summary", value: optionSummary, html: true })))));
                            }))))),
                    (detailFields.length > 0 || variantDetailFields.length > 0) && (React.createElement(TabPanel, { className: "sale-item-viewer-panel", label: t('Details') },
                        React.createElement("div", { className: "sale-item-viewer-panel-content sale-item-viewer-panel-content--scrollable" },
                            React.createElement("table", { className: "sale-item-viewer-panel-content-specifications-table" },
                                React.createElement("tbody", null,
                                    detailFields.map((value, index) => {
                                        const formattedValue = formatFieldValue(value.value, value.field.type, value.field, langCode, true);
                                        return (React.createElement("tr", { key: `item-${index}` },
                                            React.createElement("td", null, value.field.title),
                                            React.createElement("td", null, Array.isArray(formattedValue) ? (React.createElement("ul", null, formattedValue.map((v, i) => (React.createElement("li", { key: i }, v))))) : value.field.type === 'URL' ? (React.createElement("a", { href: formattedValue, target: "_blank", rel: "noreferrer" },
                                                React.createElement(Text, { className: "sale-item-viewer-infos", value: formattedValue }))) : (React.createElement("div", { className: "sale-item-viewer-infos", dangerouslySetInnerHTML: {
                                                    __html: formattedValue
                                                } })))));
                                    }),
                                    variantDetailFields.map((value, index) => {
                                        const formattedValue = formatFieldValue(value.value, value.field.type, value.field, langCode, true);
                                        return (React.createElement("tr", { key: `variant-${index}` },
                                            React.createElement("td", null, value.field.title),
                                            React.createElement("td", null, Array.isArray(formattedValue) ? (React.createElement("ul", null, formattedValue.map((v, i) => (React.createElement("li", { key: i }, v))))) : value.field.type === 'URL' ? (React.createElement("a", { href: formattedValue, target: "_blank", rel: "noreferrer" },
                                                React.createElement(Text, { className: "sale-item-viewer-infos", value: formattedValue }))) : (React.createElement("div", { className: "sale-item-viewer-infos", dangerouslySetInnerHTML: {
                                                    __html: formattedValue
                                                } })))));
                                    })))))),
                    filesFiltered && filesFiltered.length > 0 && (React.createElement(TabPanel, { className: "sale-item-viewer-panel", label: t('Files') },
                        React.createElement("div", { className: "sale-item-viewer-panel-content sale-item-viewer-panel-content--scrollable" },
                            React.createElement(Table, { className: "sale-item-viewer-file-table", name: "sale-item-viewer-file-table", schema: schema, values: filesFiltered, sortable: ['c1'], primaryColumn: "c1", actionsColumn: "c2" })))))))));
}
/**
 * @component SaleItemViewerGallery
 * @since 1.0.0
 * @hidden
 */
function SaleItemViewerGallery(props) {
    var _a;
    const { host } = useDomain();
    const { item, has3DViewer } = props;
    const { image } = item;
    let has3DViewerCond = has3DViewer;
    let viewer3dPosition = 'start';
    if (item.sku !== 'E001' &&
        item.sku !== 'E002' &&
        item.sku !== 'E003' &&
        item.sku !== 'E004' &&
        item.sku !== 'P001' &&
        item.sku !== 'P002' &&
        item.sku !== 'PRO-001' &&
        item.sku !== 'PRO-0001' &&
        item.sku !== 'PRO-00001' &&
        item.sku !== 'PRO-0021' &&
        item.sku !== 'PRO-001' &&
        item.sku !== 'PRO-002' &&
        item.sku !== 'PRO-003' &&
        item.sku !== 'PRO-004' &&
        item.sku !== 'SER-001') {
        has3DViewerCond = 'FALSE';
    }
    if (item.sku === 'P002' || item.sku === 'E002') {
        viewer3dPosition = 'end';
    }
    const [selectedImage, setImage] = useState(image);
    const [viewerDisplay, setViewerDisplayed] = useState(has3DViewerCond === 'TRUE' && viewer3dPosition === 'start' ? '3d' : 'image');
    useEffect(() => {
        if (image == null) {
            return;
        }
        setImage(image);
    }, [image]);
    if (image == null || selectedImage == null) {
        return null;
    }
    if (item.id === '93c1d08a5c284e839eb41032b2e167a0') {
        has3DViewerCond = 'TRUE';
    }
    const thumbClassName = (active) => classNames('sale-item-viewer-gallery-thumb', {
        'sale-item-viewer-gallery-thumb--active': active
    });
    const virtualTourThumb = (source) => (React.createElement("button", { type: "button", className: thumbClassName(viewerDisplay === '3d'), "aria-pressed": viewerDisplay === '3d', "aria-label": t('3D viewer'), onClick: () => setViewerDisplayed('3d') },
        React.createElement(Image, { source: source })));
    // With a 3D viewer, a single image still needs a thumb to switch back to it.
    const hasImageThumbs = ((_a = item.images) === null || _a === void 0 ? void 0 : _a.length) > (has3DViewerCond === 'TRUE' ? 0 : 1);
    const hasThumbs = hasImageThumbs || has3DViewerCond === 'TRUE';
    return (React.createElement("div", { className: "sale-item-viewer-gallery" },
        React.createElement("div", { className: "sale-item-viewer-gallery-body" },
            viewerDisplay === 'image' && (React.createElement("div", { className: "sale-item-viewer-gallery-image-viewer" },
                React.createElement(Image, Object.assign({ className: "sale-item-viewer-gallery-image", lazy: true }, imageSources(selectedImage.image, VIEWER_SIZES))))),
            has3DViewerCond === 'TRUE' && (React.createElement("div", { className: classNames('sale-item-viewer-gallery-3d-viewer', viewerDisplay === '3d' &&
                    'sale-item-viewer-gallery-3d-viewer--active') }, host === 'alpha.centralio.com' ? (React.createElement("iframe", { src: item.sku === 'PRO-001'
                    ? `https://my.matterport.com/show/?play=1&lang=en-US&m=bexXu5N9uEE`
                    : item.sku === 'PRO-002'
                        ? `https://my.matterport.com/show?play=1&lang=en-US&m=JA3mb163CC7`
                        : item.sku === 'PRO-003'
                            ? `https://my.matterport.com/show/?play=1&lang=en-US&m=neXg8rJoK8f`
                            : item.sku === 'PRO-004'
                                ? `https://my.matterport.com/show/?play=1&lang=en-US&m=CatXtjryamA`
                                : `https://my.matterport.com/show/?play=1&lang=en-US&m=CatXtjryamA`, title: "3D Viewer", allowFullScreen: true })) : host === 'axhotel.centralio.com' &&
                item.id === '93c1d08a5c284e839eb41032b2e167a0' ? (React.createElement("video", { autoPlay: true, loop: true, muted: true, playsInline: true, className: "sale-item-viewer-gallery-3d-viewer-video" },
                React.createElement("source", { src: `https://centralio-storage-app.s3.us-east-1.amazonaws.com/storage/f1e0dbb0254d4ceb986b2f150e5ed075/medias/AX+E_VE_NEMENTIEL_MIX.mov`, type: "video/mp4" }),
                "Your browser does not support the video tag")) : (React.createElement("iframe", { src: item.sku === 'E001' ||
                    item.sku === 'E002' ||
                    item.sku === 'E004'
                    ? `https://my.mpskin.com/en/tour/4rx7pzdzx?play=1&sr=%2C-.67&ss=2`
                    : item.sku === 'E003'
                        ? `https://my.mpskin.com/tour/mmhyb7pknx?play=1&sr=,-1.02&ss=263`
                        : item.sku === 'PRO-0001'
                            ? `https://www.google.com/maps/embed?pb=!1m0!3m2!1sfr!2sus!4v1453388479370!6m8!1m7!1sOAtVoylbadEAAAQzQAC9ZQ!2m2!1d46.81467687723395!2d-71.20301833631959!3f186!4f0!5f0.7820865974627469`
                            : item.sku === 'PRO-00001'
                                ? `https://my.matterport.com/show/?play=1&share=0&m=HimfFbKnZsc`
                                : item.sku === 'PRO-0021'
                                    ? `https://my.matterport.com/show/?m=gCGPNGxU15K&lang=fr&play=1&tourcta=0`
                                    : item.sku === 'PRO-001'
                                        ? `https://my.matterport.com/show/?play=1&share=0&m=MHGS6MNoVpW`
                                        : item.sku === 'SER-001'
                                            ? `https://centralio-storage-app.s3.us-east-1.amazonaws.com/storage/d16534831f8e4aa2af21b59d7612875e/medias/02-2_TECHNOLOGIE_JEAN-GAB_5-4-PAGE-SERVICE.mp4`
                                            : `https://my.matterport.com/show/?m=QWRghZsnNzi`, title: "3D Viewer", allowFullScreen: true }))))),
        hasThumbs && (React.createElement("div", { className: "sale-item-viewer-gallery-thumbs" },
            has3DViewerCond === 'TRUE' &&
                viewer3dPosition === 'start' &&
                virtualTourThumb(item.id === '93c1d08a5c284e839eb41032b2e167a0'
                    ? asset('images/video.svg')
                    : asset('images/centralio-virtual-tour.png')),
            hasImageThumbs &&
                item.images.map((image) => {
                    const active = viewerDisplay === 'image' && selectedImage.id === image.id;
                    return (React.createElement("button", { key: image.id, type: "button", className: thumbClassName(active), "aria-pressed": active, onClick: () => {
                            setImage(image);
                            setViewerDisplayed('image');
                        } },
                        React.createElement(Image, { lazy: true, source: thumbnailSource(image.image) })));
                }),
            has3DViewerCond === 'TRUE' &&
                viewer3dPosition === 'end' &&
                virtualTourThumb(asset('images/centralio-virtual-tour.png'))))));
}
